import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { defaultSessionUser, loadPersistedSession } from "./sessionRepository";
import { fetchCurrentUserProfile, loginWithPassword, mapProfileToSessionUser } from "./sessionApi";
import { extractTokenRoles, hasRequiredRole } from "../../utils/jwt";

const REQUIRED_ROLE = "ROLE_ADMIN";

function buildAnonymousState() {
  return {
    isAuthenticated: false,
    accessToken: "",
    refreshToken: "",
    tokenType: "Bearer",
    expiresIn: 0,
    status: "idle",
    error: null,
    roles: [],
    profileStatus: "idle",
    user: defaultSessionUser,
  };
}

function createInitialState() {
  const persisted = loadPersistedSession();

  if (persisted?.isAuthenticated && persisted.user && persisted.accessToken) {
    try {
      const roles = extractTokenRoles(persisted.accessToken);

      if (!roles.includes(REQUIRED_ROLE)) {
        return buildAnonymousState();
      }

      return {
        isAuthenticated: true,
        accessToken: persisted.accessToken || "",
        refreshToken: persisted.refreshToken || "",
        tokenType: persisted.tokenType || "Bearer",
        expiresIn: persisted.expiresIn || 0,
        status: "idle",
        error: null,
        roles,
        profileStatus: "idle",
        user: {
          ...defaultSessionUser,
          ...persisted.user,
        },
      };
    } catch (error) {
      return buildAnonymousState();
    }
  }

  return buildAnonymousState();
}

export const loginThunk = createAsyncThunk(
  "session/loginThunk",
  async ({ email, password }, thunkApi) => {
    try {
      const response = await loginWithPassword({ email, password });

      if (!response?.accessToken) {
        return thunkApi.rejectWithValue("Login succeeded but no access token was returned.");
      }

      let roles = [];

      try {
        roles = extractTokenRoles(response.accessToken);
      } catch (error) {
        return thunkApi.rejectWithValue("Login succeeded but the access token is invalid.");
      }

      if (!hasRequiredRole(response.accessToken, REQUIRED_ROLE)) {
        return thunkApi.rejectWithValue(
          "Access denied. Only admin accounts can sign in to this application."
        );
      }

      let profileRecord = null;

      try {
        profileRecord = await fetchCurrentUserProfile(response.accessToken);
      } catch (error) {
        return thunkApi.rejectWithValue(
          error?.message || "Signed in, but failed to load the admin profile."
        );
      }

      return {
        accessToken: response.accessToken,
        refreshToken: response.refreshToken || "",
        tokenType: response.tokenType || "Bearer",
        expiresIn: Number(response.expiresIn) || 0,
        roles,
        user: mapProfileToSessionUser(profileRecord, roles),
      };
    } catch (error) {
      return thunkApi.rejectWithValue(
        error?.message || "Unable to sign in with the provided credentials."
      );
    }
  }
);

export const fetchCurrentUserThunk = createAsyncThunk(
  "session/fetchCurrentUserThunk",
  async (_, thunkApi) => {
    const state = thunkApi.getState();
    const accessToken = String(state?.session?.accessToken || "").trim();
    const roles = Array.isArray(state?.session?.roles) ? state.session.roles : [];

    if (!accessToken) {
      return thunkApi.rejectWithValue("No active session token was found.");
    }

    try {
      const profileRecord = await fetchCurrentUserProfile(accessToken);
      return mapProfileToSessionUser(profileRecord, roles);
    } catch (error) {
      return thunkApi.rejectWithValue(
        error?.message || "Unable to load the current admin profile."
      );
    }
  }
);

const sessionSlice = createSlice({
  name: "session",
  initialState: createInitialState(),
  reducers: {
    login: (state, action) => {
      const payload = action.payload || {};
      state.isAuthenticated = true;
      state.accessToken = payload.accessToken || "";
      state.refreshToken = payload.refreshToken || "";
      state.tokenType = payload.tokenType || "Bearer";
      state.expiresIn = Number(payload.expiresIn) || 0;
      state.status = "succeeded";
      state.error = null;
      state.roles = Array.isArray(payload.roles) ? payload.roles : [];
      state.user = {
        ...defaultSessionUser,
        ...payload,
      };
    },
    logout: (state) => {
      state.isAuthenticated = false;
      state.accessToken = "";
      state.refreshToken = "";
      state.tokenType = "Bearer";
      state.expiresIn = 0;
      state.status = "idle";
      state.error = null;
      state.roles = [];
      state.profileStatus = "idle";
      state.user = defaultSessionUser;
    },
    updateProfile: (state, action) => {
      state.user = {
        ...state.user,
        ...action.payload,
      };
    },
    clearSessionError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loginThunk.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(loginThunk.fulfilled, (state, action) => {
        state.isAuthenticated = true;
        state.accessToken = action.payload.accessToken;
        state.refreshToken = action.payload.refreshToken;
        state.tokenType = action.payload.tokenType;
        state.expiresIn = action.payload.expiresIn;
        state.status = "succeeded";
        state.error = null;
        state.roles = action.payload.roles || [];
        state.profileStatus = "succeeded";
        state.user = {
          ...defaultSessionUser,
          ...action.payload.user,
        };
      })
      .addCase(loginThunk.rejected, (state, action) => {
        state.status = "failed";
        state.profileStatus = "idle";
        state.error =
          action.payload || action.error.message || "Unable to sign in.";
      })
      .addCase(fetchCurrentUserThunk.pending, (state) => {
        state.profileStatus = "loading";
      })
      .addCase(fetchCurrentUserThunk.fulfilled, (state, action) => {
        state.profileStatus = "succeeded";
        state.user = {
          ...defaultSessionUser,
          ...action.payload,
        };
      })
      .addCase(fetchCurrentUserThunk.rejected, (state) => {
        state.profileStatus = "failed";
      });
  },
});

export const { login, logout, updateProfile, clearSessionError } = sessionSlice.actions;

export function selectSessionSnapshot(rootState) {
  const session = rootState.session;

  return {
    isAuthenticated: session.isAuthenticated,
    accessToken: session.accessToken,
    refreshToken: session.refreshToken,
    tokenType: session.tokenType,
    expiresIn: session.expiresIn,
    roles: session.roles,
    user: session.user,
  };
}

export default sessionSlice.reducer;
